import { asDay, berichtsbezugVon, buchungstag, isAccountingEntry } from './serviceLogUtils';
import { isInspectionProofEntry } from './flatRateLogic';

/**
 * Versendeter Begehungsbericht OHNE Leistungsbuchung — die Regeln.
 *
 * WARUM ES DIESES MODUL GIBT
 * --------------------------
 * Die Kette lautet: Bericht archivieren → VERSENDEN (erst hier wird gebucht)
 * → Protokolleintrag → quantity_used → offene Menge → Rechnungszeile. Reisst
 * sie zwischen Versand und Protokolleintrag, geht die Mail trotzdem raus und
 * die Begehung erscheint auf keiner Rechnung — ohne Fehlermeldung.
 *
 * Gewarnt wurde davor bisher genau einmal, im Sendemoment: `EmailModal` fragt
 * ueber `sendWouldBookNothing` zurueck, `reportBooking.ts` liefert dieselbe
 * Lage als `BookingSkipNotice` mit dem Grund `no_booking_target`. Beides ist
 * ein FENSTER — wer es wegklickt, hinterlaesst keine Spur. Dreimal belegt:
 * Projekt 26071015 (Bericht #1, 225 EUR), 26041014 (Bericht #13, 160 EUR),
 * 6051 (Berichte #108/#109).
 *
 * Erkannt wird die Lage jetzt SERVERSEITIG und flottenweit, in der Sicht
 * `public.v_unbooked_sent_reports` (supabase/unbooked_sent_reports.sql,
 * MIGRATIONS.md Nr. 45). Das Praedikat der Sicht steht unten als
 * `isInspectionBilled` — bewusst ENGER als `isReportBooked`, weil beide eine
 * andere Frage stellen; die Begruendung steht dort. Ausserdem steht hier,
 * was aus einer gemeldeten Zeile eine ANZEIGBARE AUFGABE macht.
 *
 * Seit Nr. 58 (supabase/unbooked_reports_legacy.sql) kennt das Praedikat
 * zusaetzlich die ALTEN Buchungsschreibweisen. Ohne sie waren 91 % der
 * Meldungen Fehlalarme — und wer einen Fehlalarm „nachbucht", bucht die
 * Begehung ein zweites Mal. Die Messwerte stehen unten am dritten Zweig.
 *
 * Pure Funktionen, weil das Projekt keine DOM-Testumgebung hat (kein jsdom) —
 * stehende Entscheidung „duenne Container, Logik in Hooks + pure Funktionen".
 * Alles, was hier steht, ist ohne Supabase und ohne React pruefbar.
 */

/**
 * Ein Protokolleintrag, soweit dieses Modul ihn braucht.
 *
 * `date`, `quantity` und `bookingReference` kamen mit dem dritten Zweig dazu:
 * die alten Schreibweisen tragen keinen `reportRef`, an dem sie sich sicher
 * erkennen liessen — bei ihnen entscheidet die Beschreibung ZUSAMMEN mit
 * Menge, Referenz und Datum, ob es eine Buchung dieser Begehung ist.
 */
export interface BillingLogEntry {
    /** Nur fuer den Berichtsbezug aus dem Journal (S5c, `berichtsbezugVon`). */
    id?: string;
    reportRef?: string;
    description?: string;
    /**
     * Leistungsdatum. Traegt im Bestand oft einen vollen Zeitstempel
     * ("2026-07-31T14:39:00.000Z"), waehrend das Berichtsdatum ein reiner Tag
     * ist. Nur ueber `asDay` vergleichen — der rohe Zeichenkettenvergleich
     * hat in diesem Projekt schon einmal jede Leistung des Monatsletzten
     * verschluckt (siehe serviceLogUtils.asDay).
     */
    date?: string;
    quantity?: number;
    bookingReference?: string;
}

/**
 * Fensterbreite des dritten Zweigs: ±30 Tage um das Berichtsdatum.
 *
 * Gemessen am Bestand: echte Treffer enden bei 15 Tagen (5783 #4), der
 * naechste Eintrag ueberhaupt liegt erst bei 42 Tagen. Zwischen 16 und 41
 * liegt NICHTS. ±30 ist damit genauso sicher wie ±14 und raeumt einen echten
 * Fall mehr. Eine systematische Monatsend-Sammelbuchung, die das Fenster
 * gefaehrlich machen wuerde, gibt es nicht.
 */
export const LEGACY_BOOKING_WINDOW_DAYS = 30;

/**
 * Abstand zweier Tage in Tagen — oder `undefined`, wenn einer davon fehlt
 * oder unlesbar ist.
 *
 * Beide Seiten laufen durch `asDay`, damit ein Zeitstempel nicht ueber die
 * Zeichenkette gegen einen reinen Tag verglichen wird.
 */
const dayDistance = (a?: string | null, b?: string | null): number | undefined => {
    const tagA = asDay(a || undefined);
    const tagB = asDay(b || undefined);
    if (!tagA || !tagB) return undefined;
    const msA = Date.parse(`${tagA}T00:00:00Z`);
    const msB = Date.parse(`${tagB}T00:00:00Z`);
    if (isNaN(msA) || isNaN(msB)) return undefined;
    return Math.abs(msA - msB) / 86_400_000;
};

/**
 * Nennt diese Beschreibung die Berichtsnummer — und zwar GENAU sie?
 *
 * Die negative Vorschau trennt die Unternummern ab: „Bericht Nr. 42.1" ist
 * ein eigener Vorgang und zaehlt NICHT fuer Bericht 42 (9 solcher Eintraege
 * in 6051 und 6172). Ebenso wenig ist „Bericht #14" eine Buchung fuer
 * Bericht 1.
 *
 * Wortgleich zur Sicht: `~ ('(#|\mNr\.?\s*)' || nr || '(?![0-9]|\.[0-9])')`.
 */
const mentionsInspectionNumber = (description: string, inspectionNumber: number): boolean =>
    new RegExp(`(#|\\bNr\\.?\\s*)${inspectionNumber}(?!\\d|\\.\\d)`).test(description);

/**
 * Ist diese Zeile im Protokoll der Mutter die Buchung einer TOCHTER? (Nr. 137)
 *
 * Wortgleich zur Sicht: `report_child_project_id is not null or report_ref ~
 * '^RPT-CHILD-' or description ~ '^Tochter-Projekt'`. Die Kind-Spalte setzt
 * jeder Schreiber nur aus dem Ref `RPT-CHILD-<uuid>-<N>` (parseReportRef,
 * Nr. 60/120, getrimmt) — hier deckt sie der Ref mit ab.
 *
 * Bewusst NICHT `istKindBuchung` (linkedBillingMode.ts): das verlangt
 * `Tochter-Projekt "` MIT Anfuehrungszeichen. Der Altbestand kennt die Form
 * ohne — „Tochter-Projekt SuedLink Los 5: Bericht Nr. 18" (6172-1 #18) ist die
 * Buchung dieser Tochter, und sie als Mutterzeile zu lesen, meldete einen
 * gebuchten Bericht als offen. Wer ihn dann nachbucht, bucht ihn doppelt.
 */
const istKindZeile = (e: BillingLogEntry): boolean =>
    String(e.reportRef || '').trim().startsWith('RPT-CHILD-')
    || String(e.description || '').startsWith('Tochter-Projekt');

/**
 * Ist die BEGEHUNG dieses Berichts abgerechnet?
 *
 * Die pruefbare Fassung des Praedikats, das die Sicht
 * `public.v_unbooked_sent_reports` in SQL fuehrt. Wer eine der beiden
 * Fassungen aendert, aendert beide — der SQL-Kopf verweist hierher.
 *
 * ─── WARUM DAS NICHT `isReportBooked` IST ────────────────────────────────
 * Weil es eine ANDERE FRAGE ist, nicht eine zweite Antwort auf dieselbe:
 *
 *   `isReportBooked` (reportBooking.ts) ist ein WIEDERHOLUNGSVERSAND-RIEGEL:
 *   „wurde fuer diesen Bericht ueberhaupt schon irgendetwas gebucht?" Diese
 *   Frage MUSS grosszuegig bleiben, sonst bucht ein zweiter Versand die
 *   Zusatzleistungen ein weiteres Mal. Dass `book()` den `reportRef` an JEDE
 *   Zeile schreibt — auch an eine reine Zusatzleistung — ist dafuer genau
 *   richtig.
 *
 *   Hier lautet die Frage: „ist die BEGEHUNG SELBST abgerechnet?" Darauf ist
 *   eine Zusatzleistung keine Antwort.
 *
 * `isReportBooked` bleibt deshalb bewusst GROSSZUEGIG: es sitzt in einem
 * Bug-Magneten (EmailModal, `sendWouldBookNothing`), und das Verhalten eines
 * Wiederholungsversands zu aendern waere ein Risiko ohne Not. (S5c stellt
 * beide auf die Journal-Spalten um, ohne an dieser Trennung etwas zu aendern.)
 *
 * ─── S5c: ZWEIG 1 UND 2 AUS DEN SPALTEN ──────────────────────────────────
 * Ist der Berichtsbezug einer Zeile aus dem Journal bekannt
 * (`berichtsbezugVon`: sichere Nummer, Fingerabdruck passt), entscheiden fuer
 * sie statt der beiden ersten Zweige die Spalten:
 *   covers_inspection UND report_number = N UND keine Kind-Zeile
 * Den Abschnitt fragen sie nicht — die Berichtsnummer ist projektweit, der
 * Abschnitt einer Buchung und der des Berichts koennen auseinanderlaufen
 * (Begruendung an `isReportBooked`). Der dritte Zweig bleibt fuer JEDE Zeile
 * Text — genau wie Riegel 3 der Sicht.
 *
 * Gegenueber den beiden Textzweigen:
 *   - ENGER: kanonischer Text ohne Deckung (Menge 0, Verbuchung) und eine
 *     Kind-Zeile mit kanonischem Text. Die Sicht meldet dort nicht (Zweig 1
 *     nennt sie gebucht) — auf ihren GEMELDETEN Zeilen aendert das nichts.
 *   - WEITER: eine deckende Zeile mit dem Ref eines ANDEREN Abschnitts und
 *     ohne kanonischen Text (`book()` schreibt immer den kanonischen, dann
 *     griffe Zweig 1 ohnehin), dazu Refs ausserhalb der Normalform
 *     (`RPT-all-07`, Leerzeichen). Meldet die Sicht einen solchen Bericht,
 *     sperrt das Nachbuchen mit „bereits gebucht" — die sichere Richtung:
 *     es entsteht keine zweite Buchung desselben Berichts.
 * Anzahl beider Richtungen im Bestand: Abnahme von Nr. 119 (Zeilen 9, 10).
 *
 * ─── LOCKSTEP MIT DER SICHT (S6, Nr. 133) ────────────────────────────────
 * Ab Nr. 133 (supabase/unbooked_reports_journal.sql) liest die Sicht
 * `v_unbooked_sent_reports` das JOURNAL statt des JSON (Funktion
 * `intern.bericht_status`) — mit derselben REGEL: lebende Zeilen mit
 * sicherer Nummer (exact/text_exact) entscheiden ueber die Spalten (deckt,
 * Nummer, keine Kind-Zeile, kein Abschnitt), jede andere ueber den Text der
 * Zweige 1 und 2; der dritte Zweig bleibt fuer jede Zeile Text. Gleich ist
 * die Regel, nicht jede Entscheidung — drei Stellen laufen auseinander:
 *   - WANN die Spalten entscheiden: die Sicht nimmt sie fuer JEDE
 *     Journalzeile mit sicherer Nummer; hier nur, wenn das Register geladen
 *     ist und der Fingerabdruck des Eintrags passt (`berichtsbezugVon`) —
 *     sonst entscheidet hier der Text.
 *   - WELCHE Zeilen: das JSON hier traegt auch Eintraege, die Nr. 60 nicht
 *     ins Journal abbilden konnte (Quarantaene `nicht_abbildbar`: kein oder
 *     ungueltiges Datum, keine serviceId, unlesbare Menge). Hier koennen sie
 *     einen Bericht raeumen, in der Sicht nicht.
 *   - Das Fenster des dritten Zweigs: die Sicht zaehlt am Berliner Tag
 *     (`entry_day`), hier zaehlt `asDay` (Textschnitt, bei vollem
 *     Zeitstempel also der UTC-Tag).
 *   - TOCHTERBERICHTE (`parentProjectId` gesetzt): hier gibt es nur EIN
 *     Protokoll, das der Mutter. Riegel 1 der Sicht (Hauptbuchung im Journal
 *     der Tochter selbst) bildet diese Funktion deshalb nicht ab, Riegel 2
 *     (Kind-Ref dieser Tochter, ohne Menge und Fenster) nur ueber Zweig 3
 *     (Kind-Zeile mit Menge im Fenster). Hier wird also eher GEMELDET als in
 *     der Sicht — die sichere Richtung. Kein Produktionspfad ruft diese
 *     Funktion fuer eine Tochter: das Nachbuchen prueft Toechter ueber den
 *     Kind-Ref (reportRebookLogic, reportRebookService).
 * Wer hier eine Bedingung aendert, aendert Nr. 133 mit (neue Nummer,
 * `create or replace`) — zuletzt Nr. 137 (Kind-Ausschluss im dritten Zweig,
 * supabase/unbooked_reports_kind_ausschluss.sql).
 *
 * ─── DAS PRAEDIKAT ───────────────────────────────────────────────────────
 *   description = 'Bericht #<N> abgeschlossen'
 * ODER
 *   reportRef = 'RPT-<sectionId|all>-<N>' UND description <> 'Zusatzleistung: Bericht #<N>'
 * ODER
 *   eine ALTE Buchungsschreibweise im Fenster (dritter Zweig, siehe unten)
 *
 * Erster Zweig fuer den Altbestand (kein `reportRef` vorhanden), zweiter als
 * Netz fuer Ref-Zeilen mit abweichender Beschreibung. Am Code geprueft:
 * `book()` wird nur mit `mainDesc` und `addonDesc` gerufen — auch die
 * Aufteilung auf mehrere Lose erzeugt ausschliesslich `mainDesc`-Zeilen —,
 * und die Reparaturskripte des Repos schreiben ebenfalls `mainDesc`. Der
 * zweite Zweig deckt im sichtbaren Bestand also nichts ab, was der erste
 * nicht schon deckt; er steht fuer Zeilen aus Wegen, die das Repo nicht kennt.
 *
 * Fehlende Beschreibung zaehlt als NICHT-Zusatzleistung: eine Ref-Zeile ohne
 * Beschreibung ist keine Zusatzbuchung.
 *
 * ─── DER DRITTE ZWEIG: ALTE BUCHUNGSSCHREIBWEISEN ────────────────────────
 * Die beiden ersten Zweige kennen nur die Schreibweise, die `book()` HEUTE
 * erzeugt. Am 30.08.2026 gegen PROD gemessen: die Sicht meldete 673
 * versendete Berichte ohne Buchung — und zu 604 davon stand sehr wohl ein
 * Leistungseintrag im Fenster, der die Berichtsnummer nennt, bei 547 sogar
 * am SELBEN TAG. Gezaehlt wurde nur mit Menge > 0 und ohne
 * Buchhaltungspraefix.
 *
 * Die Buchungen existieren also; sie tragen bloss aeltere Beschreibungen:
 *
 *   "Bericht #14 versendet (Cloud)"        (6258, LEGACY-AUTO)
 *   "Bericht #11 generiert"                (6258)
 *   "Bericht Nr. 44"                       (6051)
 *   "Bericht Nr. 22 (08:00 Uhr)"           (6172)
 *   "Tochter-Projekt \"X\": Bericht Nr. 5" (Kind-zu-Mutter-Pfad)
 *
 * 91 % Fehlalarm ist keine Unschoenheit, sondern gefaehrlich: wer eine solche
 * Zeile „nachbucht", bucht die Begehung ein ZWEITES Mal — sie steht dann
 * doppelt im Leistungsstand und doppelt auf der Rechnung.
 *
 * Ein Eintrag zaehlt als Altbuchung, wenn ALLE sechs Bedingungen zutreffen:
 *
 *  (1) `quantity > 0` — eine Zeile ohne Menge bewegt keinen Leistungsstand
 *      und ist damit kein Nachweis, dass die Begehung abgerechnet ist.
 *  (2) `bookingReference` traegt KEIN Buchhaltungspraefix (`BU-`, `VZ-`,
 *      `RN-`, `MB-`, via `isAccountingEntry`). Das sind Abrechnungs-,
 *      Verzichts- und Ruecknahmevorgaenge, keine erbrachte Leistung.
 *  (3) Die Beschreibung beginnt NICHT mit „Zusatzleistung". Das ist ein
 *      BLOCKER, kein Feinschliff: „Zusatzleistung: Bericht #N" erfuellt (1),
 *      (2) und (5) muehelos — 21 solcher Eintraege treffen im Fenster auf 14
 *      Sichtzeilen (5335, 5748, 5940, 5956, 6070, 6070-3, 6105, 6125). Ohne
 *      den Ausschluss waere der KERNFALL dieser Sicht — „nur die
 *      Zusatzleistung wurde gebucht, die Begehung selbst nicht" —
 *      strukturell unsichtbar. Dass es heute gutginge, ist Zufall: alle 14
 *      haben daneben noch eine echte Zeile.
 *  (4) Fuer einen Bericht OHNE Mutterprojekt: die Beschreibung beginnt nicht
 *      mit „Tochter-Projekt". Im eigenen Protokoll bezeichnet eine solche
 *      Zeile den Bericht einer TOCHTER, nicht den eigenen — sonst raeumte
 *      die Buchung eines Kindes die Meldung der Mutter weg. Kollisionen im
 *      Bestand gemessen: 0 — der Ausschluss kostet nichts und schliesst die
 *      Klasse trotzdem.
 *      Fuer einen TOCHTERbericht umgekehrt (seit Nr. 137): gesucht wird im
 *      Protokoll der MUTTER, und dort zaehlt NUR eine Kind-Zeile
 *      (`istKindZeile`: Ref `RPT-CHILD-…` oder „Tochter-Projekt…"). Bis
 *      Nr. 137 zaehlte dort jede Zeile — auch die eigene Hauptbuchung der
 *      Mutter, die ihren gleichnummerigen Bericht bucht, nicht den der
 *      Tochter. PROD 02.10.2026: 18 Tochterberichte galten nur so als gebucht
 *      (26021023-2 #1–#10, 6172-1 #10–#17), alle Tagespauschale und am Tag
 *      ueber die Mutter gedeckt (`istTagGedeckt`) — Geld heute 0, gebaut fuer
 *      einzeln abrechnende Toechter. Verlangt ist IRGENDEINE Kind-Zeile, nicht
 *      die DIESER Tochter: der Altbestand traegt weder Ref noch immer den
 *      Namen in Anfuehrungszeichen (6172-1 #18), „diese Tochter" meldete dort
 *      einen gebuchten Bericht als offen. Die Zeile einer Schwester mit
 *      derselben Nummer im Fenster raeumt deshalb weiter (gemessen: keine).
 *  (5) Die Beschreibung nennt die Berichtsnummer, Unternummern getrennt
 *      (`mentionsInspectionNumber`).
 *  (6) `date` liegt innerhalb von ±`LEGACY_BOOKING_WINDOW_DAYS` Tagen um das
 *      Berichtsdatum, verglichen ueber `asDay`.
 *
 * ─── OHNE BERICHTSDATUM ──────────────────────────────────────────────────
 * Ist `inspectionDate` unbekannt (oder unlesbar), greift der dritte Zweig
 * GAR NICHT — die beiden ersten arbeiten unveraendert weiter. Ohne Datum
 * laesst sich (6) nicht pruefen, und ein Zweig, der dann nur noch ueber die
 * Berichtsnummer ginge, raeumte eine Meldung schon wegen irgendeiner Zeile
 * „Bericht Nr. 44" aus einem anderen Jahr weg. Die sichere Richtung ist hier
 * eindeutig: eine Meldung stehen zu lassen kostet eine Pruefung von Hand,
 * sie falsch zu raeumen kostet eine Begehung auf der Rechnung.
 *
 * @param inspectionDate Begehungsdatum aus dem Archiv (`inspection_date`).
 * @param parentProjectId Mutterprojekt der Berichtszeile, falls es eine
 *   Tochter ist. Steuert (4) und schaltet fuer die Tochter die Zweige 1 und 2
 *   ab (Nr. 137) — der `serviceLog` selbst muss der des BUCHUNGSPROJEKTS sein
 *   (`coalesce(parent_project_id, project_id)`).
 */
export const isInspectionBilled = (
    serviceLog: BillingLogEntry[] | undefined,
    inspectionNumber: number,
    sectionId?: string | null,
    inspectionDate?: string | null,
    parentProjectId?: string | null,
): boolean => {
    const ref = `RPT-${sectionId || 'all'}-${inspectionNumber}`;
    const mainDesc = `Bericht #${inspectionNumber} abgeschlossen`;
    const addonDesc = `Zusatzleistung: Bericht #${inspectionNumber}`;

    // Zweig 1 und 2 — aus den Spalten, wenn der Berichtsbezug bekannt ist.
    const isMainOrRefBooking = (e: BillingLogEntry): boolean => {
        const bezug = berichtsbezugVon(e);
        if (bezug) return bezug.deckt && bezug.nummer === inspectionNumber && bezug.kindProjekt === null;
        return e.description === mainDesc
            || (e.reportRef === ref && (e.description || '') !== addonDesc);
    };

    const isLegacyBooking = (e: BillingLogEntry): boolean => {
        // (1) Menge
        if (!(Number(e.quantity) > 0)) return false;
        // (2) kein Buchhaltungsvorgang
        if (isAccountingEntry(e)) return false;
        const desc = e.description || '';
        // (3) keine Zusatzleistung
        if (desc.startsWith('Zusatzleistung')) return false;
        // (4) Kind-Buchung ja oder nein — je nachdem, WESSEN Bericht gefragt ist
        if (parentProjectId ? !istKindZeile(e) : desc.startsWith('Tochter-Projekt')) return false;
        // (5) nennt genau diese Berichtsnummer
        if (!mentionsInspectionNumber(desc, inspectionNumber)) return false;
        // (6) im Datumsfenster — ohne Berichtsdatum greift der Zweig nicht
        const abstand = dayDistance(e.date, inspectionDate);
        return abstand !== undefined && abstand <= LEGACY_BOOKING_WINDOW_DAYS;
    };

    // Tochterbericht: das Protokoll ist das der MUTTER. Zweig 1 und 2 fragen
    // nach der Hauptbuchung im EIGENEN Protokoll (Riegel 1 der Sicht liest das
    // Journal der Tochter selbst); im Protokoll der Mutter traefen sie deren
    // eigene Hauptbuchung „Bericht #N abgeschlossen" — den gleichnummerigen
    // Bericht der MUTTER. Ab Nr. 137 raeumt so eine Zeile keinen
    // Tochterbericht mehr; es bleibt Zweig 3, und der verlangt eine Kind-Zeile.
    return (serviceLog || []).some(e => !!e && (
        (!parentProjectId && isMainOrRefBooking(e)) || isLegacyBooking(e)));
};

/**
 * ─── Tagespauschale: die Mutter deckt den TAG, nicht den Bericht ───────────
 *
 * Bei `billingMode: 'daily_flat'` ist der KALENDERTAG die abrechenbare
 * Einheit. Begeht ein Koordinator an einem Tag die Mutter und ihre Toechter,
 * ist das EINE Leistung. Der Versandpfad haelt sich daran: findet er im
 * Protokoll der Mutter schon eine Begehungsbuchung dieses Tages, bucht die
 * Tochter NICHTS mehr (`alreadyBilledToday` in SiGeKoInspectionModule).
 *
 * Genau daraus entsteht ein Fehlalarm. Der Tochter-Riegel der Sicht
 * (`v_unbooked_sent_reports`, Riegel 2) sucht im Mutterprotokoll nach
 * `RPT-CHILD-<kindId>-<nr>` — einem Eintrag, den der richtige Ablauf bei
 * gedecktem Tag NIE schreibt. Die Sicht verlangt also einen Beleg, den es
 * per Konstruktion nicht geben kann, und meldet die Begehung als unabgerechnet.
 *
 * Nachgewiesen an Projekt 5284-1 „Duenenpark Sylt, Schwestern" (30.08.2026):
 * 13 gemeldete Berichte, alle 13 vom Protokoll der Mutter am selben Tag
 * gedeckt, Nummernversatz 13 (Tochter #107 = Mutter #94). Nachbuchen haette
 * 13 × 775 EUR = 9.775 EUR doppelt gefordert.
 *
 * ─── Warum nur bei Tagespauschale ──────────────────────────────────────────
 *
 * Bei EINZELabrechnung (`individual`) hat jede Tochter ihre eigene Leistung;
 * derselbe Tag ist dort der Normalfall und deckt gar nichts. Die Regel darf
 * deshalb NIE ohne den Modus greifen — sonst verschluckt sie bei jeder
 * einzeln abgerechneten Tochter genau die Begehungen, die zu fordern waeren.
 * Ist der Modus unbekannt, gilt der Tag als NICHT gedeckt.
 */
export const TAGESPAUSCHALE = 'daily_flat';

/**
 * Deckt das Protokoll der Mutter den Begehungstag der Tochter?
 *
 * Woertlich dieselben vier Bedingungen wie im Versandpfad: gleicher Tag,
 * Begehungsposition, Menge > 0, kein Buchhaltungsvorgang.
 *
 * ODER eine NACHWEISZEILE mit Berichtsbezug am selben Tag (30.09.2026): im
 * Pauschalprojekt bucht der Versand die Begehung der Mutter als Menge 0
 * ('NW-…') auf die Monatspauschale, die Begehungsposition bekommt nichts.
 * Menge > 0 verlangte dort einen Beleg, den es nicht mehr gibt, und meldete
 * jeden gedeckten Tochtertag als offen. 'NW-' traegt nur eine Pauschale
 * (serviceLogUtils.FLAT_PROOF_PREFIX) — deshalb ohne Positionsliste; ohne
 * Pauschale als Bezug (null) zaehlt der Nachweis MIT Bericht und die
 * Hand-Begehung ('NW-BEG-', isInspectionProofEntry), eine Handnotiz auf einer
 * 'all'-Pauschale nicht.
 *
 * @param inspektionsPositionen ids der Positionen der MUTTER mit
 *   `assignment === 'inspection'`. Gruppenweit und nicht je Position: zwei
 *   Toechter koennen verschiedene Positionen treffen und sind trotzdem
 *   derselbe Tag.
 */
export const istTagGedeckt = (
    mutterLog: BillingLogEntry[] | undefined,
    inspektionsPositionen: Set<string>,
    begehungstag?: string | null,
): boolean => {
    // S5d: beide Seiten als Tag in Europe/Berlin (`buchungstag`) — dieselbe
    // Rechnung wie im Versandpfad und in der Journal-Spalte `entry_day`. Bis
    // S5d schnitt diese Stelle den UTC-Text (`asDay`): im Nachtlauf (UTC)
    // lag eine Mutterbuchung zwischen 0 und 2 Uhr damit am Vortag, und die
    // Tochter meldete einen gedeckten Tag als offen.
    const tag = buchungstag(begehungstag);
    if (!tag) return false;
    return (mutterLog || []).some((e) =>
        !!e
        && buchungstag(e.date) === tag
        && (
            ((Number(e.quantity) || 0) > 0
                && !isAccountingEntry(e)
                && inspektionsPositionen.has(String((e as { serviceId?: string }).serviceId || '')))
            || isInspectionProofEntry(e, null)
        ),
    );
};

/**
 * ─── Wann ein Projekt aufhoert zu melden ───────────────────────────────────
 *
 * Entscheidung des Betreibers vom 30.08.2026, in zwei Schritten praezisiert:
 * still wird ein Projekt, das **im Archiv liegt UND an dem keine Rechnung mehr
 * zu schreiben ist**. Beides zusammen, nie eines allein.
 *
 * „Im Archiv" ist `projects.status = 'finished'`. Einen eigenen Status
 * `archived` gibt es NICHT — der Wert heisst ueberall „Archiv" (AdminProjectList,
 * AdminDashboard, ProjectCard, ProjectStats). Seit dem 15.09.2026 gibt es
 * davor den Status 'completed' (BEENDET): Arbeit vorbei, Schlussrechnung
 * steht aus. Ein BEENDETES Projekt meldet WEITER — dort wird gerade
 * abgerechnet, und genau da darf nichts untergehen.
 *
 * WARUM DER STATUS ALLEIN NICHT REICHT: er sagt „Bauzeit vorbei", nicht
 * „bezahlt". Am 30.08.2026 lagen in abgeschlossenen Projekten 171 offene
 * Befunde — 160 davon an Vorgaengen ohne jede offene Forderung (das gesuchte
 * Rauschen), 11 aber an dreien, die noch Geld trugen: 5983 mit 800 EUR und
 * 6136 mit 480 EUR, bei 6136 sogar ein `versendet_ohne_buchung`. Genau diese
 * elf muessen stehen bleiben, sonst verschweigt die Anwendung eine Forderung.
 *
 * Umkehrbar und ohne Datenschaden: der Waechter hoert nur auf zu MELDEN.
 * Bestehende Zeilen bekommen im naechsten Nachtlauf `resolved_at` und bleiben
 * als Archiv stehen; wird ein Projekt wieder aktiv oder taucht eine
 * unabgerechnete Menge auf, meldet der Lauf erneut.
 */
export const ABGESCHLOSSEN = 'finished';

/** Mengenrundung — dieselbe Groessenordnung wie im Projektcheck. */
const MENGEN_EPS = 0.0005;

/**
 * Ist an diesem Projekt nichts mehr abzurechnen?
 *
 * Wahr, wenn KEINE Position mehr Erbrachtes fuehrt, als abgerechnet oder
 * ausdruecklich verzichtet ist. Der Verzicht (`quantityWaived`) zaehlt
 * mit — er ist die begruendete Aussage „das wird nicht mehr gefordert" und
 * genau dafuer gebaut.
 *
 * Eine LEERE Liste gilt als ausgeglichen: ein Projekt ohne
 * Leistungsverzeichnis kann keine Forderung tragen.
 */
export const istAusgeglichen = (services: Array<{
    quantityUsed?: number;
    quantityBilled?: number;
    quantityWaived?: number;
}> | undefined | null): boolean =>
    !(services || []).some(s =>
        (s?.quantityUsed || 0) - (s?.quantityBilled || 0) - (s?.quantityWaived || 0) > MENGEN_EPS);

/**
 * Wird ueber dieses Projekt noch gemeldet?
 *
 * `ausgeglichen` ist mit Vorgabe FALSE bewusst: ein Aufrufer, der die
 * Positionen nicht kennt, meldet WEITER. Im Zweifel eine Zeile zu viel — eine
 * verschwiegene Forderung faellt niemandem auf.
 */
export const meldetNoch = (projectStatus?: string | null, ausgeglichen = false): boolean =>
    !(projectStatus === ABGESCHLOSSEN && ausgeglichen);

/** Eine Zeile der Sicht `public.v_unbooked_sent_reports`, 1:1. */
export interface UnbookedSentReportRow {
    report_history_id: string;
    project_id: string;
    project_number: string | null;
    project_name: string | null;
    project_status: string | null;
    project_owner_id: string | null;
    inspection_number: number;
    section_id: string | null;
    /** Begehungsdatum aus dem Archiv. */
    inspection_date: string | null;
    conductor_name: string | null;
    /** Erster ERFOLGREICHER Versand — ab da ist der Bericht beim Kunden. */
    first_sent_at: string;
    last_sent_at: string | null;
    send_count: number;
    /**
     * `globalData.reportExempt` des Projekts — „keine Begehungen/Berichte fuer
     * dieses Projekt". Wird NUR angezeigt, nie zum Ausblenden benutzt: der
     * Haken sagt etwas ueber Berichte, die es nicht geben soll, und nichts
     * ueber einen, der nachweislich beim Kunden ist. Die Ausnahme je Bericht
     * ist `report_history.billing_exempt` (siehe setReportBillingExempt).
     */
    report_exempt: boolean;
    /** Gesetzt, wenn dieses Projekt eine Tochter ist (Buchung liegt bei der Mutter). */
    parent_project_id: string | null;
    /**
     * Zu diesem Bericht steht eine ZUSATZLEISTUNG im Protokoll, aber keine
     * Hauptbuchung.
     *
     * Ausdruecklich KEIN Buchungsnachweis: `applyReportBookingWithNotices`
     * bucht Zusatzleistungen auch dann, wenn gar keine Hauptposition gewaehlt
     * war — genau der Fall, der beim Versand `no_booking_target` meldet.
     * Zaehlte er als erledigt, verschwiege die Liste den Vorfall.
     *
     * Er ist der GRUND, warum die Zeile ueberhaupt erscheint, und gehoert
     * deshalb sichtbar in die Detailzeile: ohne den Hinweis liest der
     * Bearbeiter die Aufgabe als Fehlalarm und hakt sie als „nicht
     * abrechenbar" ab, obwohl die Begehung offen ist.
     */
    nur_zusatzleistung_gebucht: boolean;
    days_since_send: number;
}

/**
 * Ab wann die Aufgabe als dringend gilt.
 *
 * Zwei Wochen, weil in diesem Zeitraum ein Rechnungslauf sehr wahrscheinlich
 * durch ist: danach ist die Begehung nicht mehr nur unabgerechnet, sondern
 * auf einem Beleg NICHT ENTHALTEN, der bereits beim Kunden liegt. Genau das
 * war der Vorfall 26071015 — aufgefallen vier Wochen spaeter.
 */
export const UNBOOKED_URGENT_DAYS = 14;

export type UnbookedPriority = 'urgent' | 'soon';

/**
 * Dringlichkeit einer Zeile.
 *
 * Es gibt bewusst KEINE Stufe „normal": gebucht wird beim Versand. Ist beim
 * Versand nichts gebucht worden, passiert es von allein auch nie mehr — die
 * Lage ist ab Tag 0 ein Mangel und keine Faelligkeit, die noch reifen muss.
 * Die Zeit entscheidet nur, wie teuer die Korrektur wird.
 */
export const unbookedPriority = (row: UnbookedSentReportRow): UnbookedPriority =>
    (row.days_since_send ?? 0) >= UNBOOKED_URGENT_DAYS ? 'urgent' : 'soon';

/** Tag im deutschen Format. Leer/ungueltig bleibt sichtbar statt „Invalid Date". */
export const formatUnbookedDay = (value: string | null | undefined): string => {
    if (!value) return 'unbekannt';
    const d = new Date(value);
    if (isNaN(d.getTime())) return 'unbekannt';
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

/**
 * Die Ueberschrift des Falls (Meldungsliste der Verwaltung, Integritaetswaechter).
 *
 * Sie nennt die BERICHTSNUMMER, weil das der Schluessel ist, mit dem sich der
 * Fall im Archiv, im Sendeverlauf und im Protokoll wiederfinden laesst.
 */
export const unbookedLabel = (row: UnbookedSentReportRow): string =>
    `Bericht #${row.inspection_number} versendet, aber nicht abgerechnet`;

/**
 * Die Beleglage in einer Zeile: Begehungsdatum, Versanddatum, Alter.
 *
 * Beide Daten stehen bewusst nebeneinander — sie sind oft verschieden, und
 * der Abstand zwischen ihnen ist das Erste, was man beim Nacharbeiten wissen
 * muss (der Tagesriegel in `reportBooking.ts` haengt am BEGEHUNGSTAG, die
 * Frist dieser Aufgabe am VERSANDTAG).
 */
export const unbookedDetail = (row: UnbookedSentReportRow): string => {
    const teile = [
        `Begehung vom ${formatUnbookedDay(row.inspection_date)}`,
        `versendet am ${formatUnbookedDay(row.first_sent_at)}`,
        `${row.days_since_send ?? 0} ${(row.days_since_send ?? 0) === 1 ? 'Tag' : 'Tage'} offen`,
    ];
    // Vor Abschnitt und Durchfuehrendem: das ist die Angabe, die den Fall vom
    // „gar nichts gebucht" unterscheidet und den Bearbeiter anders handeln
    // laesst. Sie steht bewusst NICHT am Ende, wo sie im abgeschnittenen Text
    // einer Meldungszeile als Erstes verschwaende.
    if (row.nur_zusatzleistung_gebucht) {
        teile.push('nur Zusatzleistung gebucht — die Begehung selbst ist NICHT abgerechnet');
    }
    if (row.section_id) teile.push(`Abschnitt ${row.section_id}`);
    if (row.conductor_name) teile.push(row.conductor_name);
    // Die Buchung einer Tochter liegt bei der Mutter. Ohne diesen Hinweis
    // sucht der Bearbeiter im falschen Projekt.
    if (row.parent_project_id) teile.push('Abrechnung über das Mutterprojekt');
    // Der Projekt-Haken „keine Begehungen/Berichte" widerspricht der Lage —
    // das gehoert vor Augen, nicht weggefiltert.
    if (row.report_exempt) teile.push('Projekt ist als „ohne Berichtsführung" markiert');
    return teile.join(' · ');
};

/** Titelzeile fuer die Meldungsliste der Verwaltung (Projekt steht dort schon daneben). */
export const unbookedAlertTitle = (row: UnbookedSentReportRow): string =>
    `${unbookedLabel(row)} (seit ${row.days_since_send ?? 0} ${(row.days_since_send ?? 0) === 1 ? 'Tag' : 'Tagen'})`;

/**
 * Sortierung: das Teuerste zuerst.
 *
 * Alter schlaegt alles — je laenger der Fall liegt, desto wahrscheinlicher ist
 * bereits eine Rechnung ohne diese Begehung beim Kunden. Danach Projekt und
 * Nummer, damit die Reihenfolge bei gleichem Alter stabil bleibt (eine Liste,
 * die bei jedem Laden anders sortiert ist, wird nicht abgearbeitet).
 */
export const sortUnbookedReports = (rows: UnbookedSentReportRow[]): UnbookedSentReportRow[] =>
    [...rows].sort((a, b) => {
        const alter = (b.days_since_send ?? 0) - (a.days_since_send ?? 0);
        if (alter !== 0) return alter;
        const projekt = (a.project_number || '').localeCompare(b.project_number || '');
        if (projekt !== 0) return projekt;
        const nummer = (a.inspection_number ?? 0) - (b.inspection_number ?? 0);
        if (nummer !== 0) return nummer;
        return a.report_history_id.localeCompare(b.report_history_id);
    });

/**
 * Ist dieser Grund als Ausnahme tragfaehig?
 *
 * Wortgleich zur Wache in `set_report_billing_exempt` (leer nach `btrim` wird
 * dort abgewiesen). Der Client fragt vorher, damit der Nutzer nicht erst nach
 * dem Roundtrip erfaehrt, dass sein Klick nichts bewirkt hat.
 *
 * Ein Grund ist Pflicht, weil eine Ausnahme ohne Begruendung im Streitfall
 * wertlos ist — und weil „nicht abrechenbar" sonst der bequemere Weg waere
 * als nachzubuchen.
 */
export const isValidExemptReason = (reason: string | null | undefined): boolean =>
    (reason || '').trim().length > 0;
